import { letzteFehler, type FehlerEintrag } from "@/lib/fehlerPuffer"

/**
 * Kontext einer Meldung aus dem Portal („Problem melden", Baustein 17).
 *
 * Alles, was ein Screenshot sonst zeigen müsste — Seite, welche Mail, welcher
 * Vorgang, welcher Mandant, welche Fehlermeldung — ohne ein einziges Bild.
 * Nur KENNUNGEN, keine Namen: IDs aus Pfad, Query und Router-State.
 */

export type ProblemKontext = {
  route: string
  ids: Record<string, string>
  tenant: string | null
  browser: string
  fenster: string
  fassung: string | null
  fehler: FehlerEintrag[]
  zeit: string
  /** Offenes Pop-up beim Melden: Bezeichnung aus `data-kontext-popup`,
   *  sonst „ohne Bezeichnung“ — nie Text aus dem Pop-up selbst (Namen). */
  popup?: string | null
}

// Eine Kennung: Zahl, UUID oder HERO-artige Nummer (SAN-534). Alles andere
// (Wörter, Namen, Suchbegriffe) bleibt draußen.
const KENNUNG = /^(\d{1,19}|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|[A-Z]{2,6}-\d{1,8}|\d+\.\d+)$/i
const ID_SCHLUESSEL = /(^id$|_id$|Id$|^(mail|email|call|anruf|contact|kontakt|project|projekt|vorgang|aufgabe|task|booking|termin|meldung|kunde|thread)$)/i

export function idsAusAdresse(
  pathname: string, search = "", state: unknown = null,
): Record<string, string> {
  const ids: Record<string, string> = {}
  const teile = pathname.split("/").filter(Boolean)
  teile.forEach((teil, i) => {
    if (i > 0 && KENNUNG.test(teil) && !KENNUNG.test(teile[i - 1])) {
      ids[teile[i - 1]] = teil
    }
  })
  const q = new URLSearchParams(search)
  q.forEach((wert, schluessel) => {
    if (schluessel !== "tenant" && ID_SCHLUESSEL.test(schluessel) && KENNUNG.test(wert)) {
      ids[schluessel] = wert
    }
  })
  // react-router legt den Navigationszustand unter `usr` ab
  const usr = (state && typeof state === "object" && "usr" in state)
    ? (state as { usr: unknown }).usr : state
  if (usr && typeof usr === "object") {
    for (const [k, v] of Object.entries(usr as Record<string, unknown>)) {
      if (ID_SCHLUESSEL.test(k) && (typeof v === "number" || (typeof v === "string" && KENNUNG.test(v)))) {
        ids[k] = String(v)
      }
    }
  }
  return ids
}

/**
 * IDs geöffneter Mails aus der Seite (#155). In der Mailliste steckt die
 * offene Mail nur im React-State — Pfad und Query sind `/emails`, es kam
 * `ids: {}` an. Jede Mail-Ansicht trägt deshalb `data-kontext-mail-id`
 * (`MailIdMarke`). Mehrere offen (Liste + Vollbild): alle, zuletzt
 * gerenderte (= oberste) zuerst.
 */
export function mailIdsAusSeite(wurzel: ParentNode = document): string[] {
  try {
    const werte = Array.from(wurzel.querySelectorAll<HTMLElement>("[data-kontext-mail-id]"))
      .map((el) => el.getAttribute("data-kontext-mail-id") || "")
      .filter((v) => /^\d{1,19}$/.test(v))
      .reverse()
    return Array.from(new Set(werte))
  } catch {
    return []
  }
}

/**
 * Kennungen geöffneter Anrufe aus der Seite. Die Anruf-Details sind ein
 * Pop-up, die Adresse bleibt `/ki-telefon` — ohne Marke käme `ids: {}` an.
 */
export function anrufIdsAusSeite(wurzel: ParentNode = document): string[] {
  try {
    const werte = Array.from(wurzel.querySelectorAll<HTMLElement>("[data-kontext-anruf-id]"))
      .map((el) => el.getAttribute("data-kontext-anruf-id") || "")
      .filter((v) => /^[A-Za-z0-9_.:-]{1,80}$/.test(v))
      .reverse()
    return Array.from(new Set(werte))
  } catch {
    return []
  }
}

/**
 * Kennungen geöffneter Aufgaben (#232). Bei #224 kam zu „Aufgabe lässt sich
 * nicht bearbeiten“ nur die Mail-ID an — welche Aufgabe, wusste niemand.
 * Die Aufgaben-Dialoge tragen `data-kontext-aufgabe-id` mit Art davor:
 * `hero:<HERO-Aufgabe>`, `lokal:<tasks.id>` (noch ohne HERO-Kennung),
 * `intern:<interne Aufgabe>`. Ohne Art wird nichts übernommen.
 */
export function aufgabeIdsAusSeite(wurzel: ParentNode = document): string[] {
  try {
    const werte = Array.from(wurzel.querySelectorAll<HTMLElement>("[data-kontext-aufgabe-id]"))
      .map((el) => el.getAttribute("data-kontext-aufgabe-id") || "")
      .filter((v) => /^(hero|lokal|intern):\d{1,19}$/.test(v))
      .reverse()
    return Array.from(new Set(werte))
  } catch {
    return []
  }
}

/** Wert für `data-kontext-aufgabe-id`: HERO-Kennung vor der lokalen. */
export function aufgabeKennung(
  heroId: number | string | null | undefined, lokaleId?: number | string | null,
): string | undefined {
  if (heroId !== null && heroId !== undefined && heroId !== "") return `hero:${heroId}`
  if (lokaleId !== null && lokaleId !== undefined && lokaleId !== "") return `lokal:${lokaleId}`
  return undefined
}

/**
 * Welches Pop-up war offen, als jemand „Problem melden“ gedrückt hat?
 * Nur die feste Bezeichnung aus `data-kontext-popup` (z. B. „Anruf-Details“).
 * Pop-ups ohne Marke erkennt man an der Vollbild-Abdunkelung — dann
 * „ohne Bezeichnung“, damit die Triage wenigstens weiß, dass eins offen war.
 */
export function popupAusSeite(wurzel: Document = document): string | null {
  try {
    const benannt = Array.from(wurzel.querySelectorAll<HTMLElement>("[data-kontext-popup]"))
      .filter((el) => !el.closest("[data-kein-foto]"))
      .map((el) => (el.getAttribute("data-kontext-popup") || "").trim())
      .filter(Boolean)
    if (benannt.length) return benannt[benannt.length - 1].slice(0, 60)
    const breite = window.innerWidth, hoehe = window.innerHeight
    const abdunkelung = Array.from(wurzel.body.querySelectorAll<HTMLElement>("div")).some((el) => {
      if (el.closest("[data-kein-foto]") || el.style.position !== "fixed") return false
      const r = el.getBoundingClientRect()
      return el.style.inset === "0px" || (r.width >= breite * 0.95 && r.height >= hoehe * 0.95 && breite > 0)
    })
    return abdunkelung ? "ohne Bezeichnung" : null
  } catch {
    return null
  }
}

function fassung(): string | null {
  try {
    const s = document.querySelector<HTMLScriptElement>('script[type="module"][src*="/assets/"]')
    const m = s?.getAttribute("src")?.match(/assets\/([^/]+\.js)/)
    return m?.[1] ?? null
  } catch {
    return null
  }
}

function aktiverMandant(search: string): string | null {
  const ausUrl = new URLSearchParams(search).get("tenant")
  if (ausUrl) return ausUrl
  try {
    return localStorage.getItem("filter_tenant") || null
  } catch {
    return null
  }
}

export function sammleKontext(): ProblemKontext {
  const { pathname, search } = window.location
  const state = typeof history !== "undefined" ? history.state : null
  const ids = idsAusAdresse(pathname, search, state)
  const offen = mailIdsAusSeite()
  if (offen.length) ids.mail = offen.join(",")
  const anrufe = anrufIdsAusSeite()
  if (anrufe.length) ids.anruf = anrufe.join(",")
  const aufgaben = aufgabeIdsAusSeite()
  if (aufgaben.length) ids.aufgabe = aufgaben.join(",")
  return {
    route: pathname,
    ids,
    tenant: aktiverMandant(search),
    browser: navigator.userAgent,
    fenster: `${window.innerWidth}×${window.innerHeight}`,
    fassung: fassung(),
    fehler: letzteFehler(),
    zeit: new Date().toISOString(),
    popup: popupAusSeite(),
  }
}
